import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from '../../../redux/actions';
import api from '../../../utils/functions/api';
import { filterMarketingEmployees } from '../utils/employee';
import { formatEmployeeThaiName } from '../../../utils/functions/employeeName';

// payload ของ patchField ใช้ camelCase (ตรงกับ param ของ updateOpportunity action) แต่
// opportunity ที่ได้จาก GET เป็น snake_case (ตรงกับ DB column) — ต้อง map ก่อน merge
// เข้า local state ทันที (onLocalUpdate) ไม่งั้นค่าที่เห็นจะไม่อัพเดตจนกว่า refetch จะเสร็จ
const FIELD_KEY_MAP = {
  projectName: 'project_name',
  contactId: 'contact_id',
  contactNameSnapshot: 'contact_name_snapshot',
  ownerId: 'owner_id',
  orderedById: 'ordered_by',
  budgetApprove: 'budget_approve',
  quotationDueDate: 'quotation_due_date',
  typeOther: 'type_other',
  googleDriveLinks: 'google_drive_links',
};

function toLocalPatch(payload) {
  const patch = {};
  Object.entries(payload).forEach(([k, v]) => {
    patch[FIELD_KEY_MAP[k] || k] = v;
  });
  return patch;
}

// แผงแก้ไขข้อมูลแบบ inline ในหน้ารายละเอียดโอกาสทางธุรกิจ (เทียบเท่า applyPatch ของ
// ProjectSingleDashboard.js) — patch ทีละฟิลด์ตรงๆ ผ่าน updateOpportunity แล้ว refetch
// (onChanged) ทันที ไม่พึ่ง redux slice กลาง กัน race condition แบบที่เจอใน state.project เดิม
// เพิ่ม onLocalUpdate (optimistic) ให้ค่าที่เห็นอัพเดตทันทีไม่ต้องรอ round-trip ของ refetch —
// ยัง refetch ต่อเสมอ (ดู CLAUDE.md gotcha เดิมของ Project) ให้ค่าจริงจาก server ชนะทับในที่สุด
export function useOpportunityDashboard({
  opportunity, onChanged, onLocalUpdate, showSnack,
}) {
  const dispatch = useDispatch();
  const contact = useSelector((state) => state.contact);
  const employee = useSelector((state) => state.employee);

  const [contactInput, setContactInput] = useState('');
  const [ownerInput, setOwnerInput] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [orderedByInput, setOrderedByInput] = useState('');
  const [orderedByName, setOrderedByName] = useState('');
  const [newAppointmentDate, setNewAppointmentDate] = useState('');
  const [newAppointmentTitle, setNewAppointmentTitle] = useState('');

  // owner_id เป็น loose reference ไป Mongo Employee (คนละ DB) ไม่มี name snapshot เก็บไว้
  // เหมือน contact — fetch ชื่อจริงตรงๆ ด้วย id นี้ (ไม่พึ่ง state.employee ที่ ownerOptions
  // ค้นหาอยู่ด้านล่าง กัน race เดียวกับที่เจอใน useOpportunityForm.js)
  useEffect(() => {
    if (!opportunity?.owner_id) {
      setOwnerName('');
      return undefined;
    }
    let cancelled = false;
    api.get(`${process.env.REACT_APP_API_URL}/employee/${opportunity.owner_id}`)
      .then(({ data }) => {
        if (!cancelled) setOwnerName(formatEmployeeThaiName(data));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [opportunity?.owner_id]);

  // ordered_by (ผู้ประสานงาน) — loose reference ไป Mongo Employee เหมือน owner_id เป๊ะ
  useEffect(() => {
    if (!opportunity?.ordered_by) {
      setOrderedByName('');
      return undefined;
    }
    let cancelled = false;
    api.get(`${process.env.REACT_APP_API_URL}/employee/${opportunity.ordered_by}`)
      .then(({ data }) => {
        if (!cancelled) setOrderedByName(formatEmployeeThaiName(data));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [opportunity?.ordered_by]);

  // ดึงลูกค้าทั้งหมดครั้งเดียว (ไม่ debounce ค้นหาทีละคำ+size:20 ต่อคำค้นแบบเดิม) — Autocomplete
  // กรอง client-side จาก options เต็มชุดเองอยู่แล้วจาก contactInput (เหตุผลเดียวกับ ownerOptions
  // ด้านล่าง — เดิมเปิด dropdown เฉยๆ ยังไม่พิมพ์อะไรก็เห็นลูกค้าไม่ครบ)
  useEffect(() => {
    dispatch(actions.contactAll({ contactType: 'client', size: 9999, page: 1 }));
  }, [dispatch]);

  // ดึงพนักงานทั้งหมดครั้งเดียว (ไม่ค้นหาแบบ debounce+size:20 ต่อคำค้นเหมือนเดิม) — เดิมพิมพ์
  // ค้นหาทีละคำจะได้แค่ 20 รายชื่อแรกที่ match ต่อครั้ง ทำให้เปิด dropdown เฉยๆ (ยังไม่พิมพ์)
  // เห็นพนักงานไม่ครบ ใช้ pattern เดียวกับตัวกรอง "Owner" ใน OpportunityList.js — Autocomplete
  // กรอง client-side จาก options เต็มชุดเองอยู่แล้วจาก inputValue ไม่ต้องยิง query ใหม่ทุกครั้ง
  useEffect(() => {
    dispatch(actions.employeeAll({ size: 9999, activeOnly: false }));
  }, [dispatch]);

  const contactOptions = useMemo(() => contact?.rows || [], [contact?.rows]);
  const ownerOptions = useMemo(() => filterMarketingEmployees(employee?.rows).map((e) => ({
    _id: e._id || e.id,
    name: formatEmployeeThaiName(e),
  })), [employee?.rows]);

  // created_by เก็บ User._id (auto ตอนสร้าง) — คนละ keyspace จาก owner_id/ordered_by ที่เก็บ
  // Employee._id ตรงๆ ต้อง match ผ่าน employee.uid แทน (เหมือน CreatorFieldContent ใน
  // CellEditModal.js) อ่านอย่างเดียวในการ์ดนี้ ไม่มี UI แก้ไข (แก้ได้ผ่านตารางแทนถ้าจำเป็นจริงๆ)
  const creatorName = useMemo(() => {
    if (!opportunity?.created_by) return '';
    const match = (employee?.rows || []).find((e) => e.uid === opportunity.created_by);
    return match ? formatEmployeeThaiName(match) : '';
  }, [employee?.rows, opportunity?.created_by]);

  const patchField = async (payload) => {
    onLocalUpdate?.(toLocalPatch(payload));
    try {
      await dispatch(actions.updateOpportunity({ id: opportunity.id, ...payload }));
      onChanged();
    } catch (err) {
      showSnack(err.message || 'บันทึกไม่สำเร็จ', 'error');
      onChanged(); // ย้อนกลับเป็นค่าจริงจาก server ถ้า patch ไม่สำเร็จ
    }
  };

  const addAppointment = async () => {
    if (!newAppointmentDate) return;
    const date = newAppointmentDate;
    const title = newAppointmentTitle.trim();
    setNewAppointmentDate('');
    setNewAppointmentTitle('');
    try {
      const created = await dispatch(actions.opportunityAddAppointment({
        id: opportunity.id, date, title: title || undefined,
      }));
      onLocalUpdate?.({
        marketing_appointments: [...(opportunity.marketing_appointments || []), created]
          .sort((a, b) => new Date(a.date) - new Date(b.date)),
      });
      onChanged();
    } catch (err) {
      showSnack(err.message || 'เพิ่มวันที่นัดไม่สำเร็จ', 'error');
    }
  };

  const removeAppointment = async (appointmentId) => {
    onLocalUpdate?.({
      marketing_appointments: (opportunity.marketing_appointments || []).filter((a) => a.id !== appointmentId),
    });
    try {
      await dispatch(actions.opportunityRemoveAppointment({ id: opportunity.id, appointmentId }));
      onChanged();
    } catch (err) {
      showSnack(err.message || 'ลบวันที่นัดไม่สำเร็จ', 'error');
      onChanged();
    }
  };

  return {
    contactInput,
    setContactInput,
    contactOptions,
    ownerInput,
    setOwnerInput,
    ownerOptions,
    ownerName,
    orderedByInput,
    setOrderedByInput,
    orderedByName,
    creatorName,
    newAppointmentDate,
    setNewAppointmentDate,
    newAppointmentTitle,
    setNewAppointmentTitle,
    patchField,
    addAppointment,
    removeAppointment,
  };
}

export default useOpportunityDashboard;
